import { useCallback, useMemo } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { InventoryItem, PaginationInfo } from '../../../types/inventory';
import {
  INV_AVAILABILITY_BLOCKED_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
  isSelectableForWarehouseTransfer,
} from '../../../constants/inventory-status-labels';
import { getInventoryRequestSelectionState } from '../../../utils/inventory/inventoryRequestSelection';

export interface UseInventoryQueryTableProps {
  items: InventoryItem[];
  pagination: PaginationInfo;
  selectedItems: string[];
  onSelectItems: (items: string[]) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  isInventoryBranchQuery?: boolean;
  isAdmin?: boolean;
  /** Columnas extra solo en la tabla de almacén (p. ej. Talla, Color, Cód. Proveedor, Largo). */
  warehouseAttributeColumnCount?: number;
  /** Inventario general: selección para solicitud de inventario. */
  isInventoryGeneralRequest?: boolean;
  lockedOriginLocationName?: string | null;
}

export const useInventoryQueryTable = ({
  items,
  pagination,
  selectedItems,
  onSelectItems,
  onItemsPerPageChange,
  isInventoryBranchQuery = false,
  isAdmin = true,
  warehouseAttributeColumnCount = 0,
  isInventoryGeneralRequest = false,
  lockedOriginLocationName = null,
}: UseInventoryQueryTableProps) => {
  // Verificar si todos los elementos seleccionables de la página actual están seleccionados
  const selectableItems = useMemo(() => {
    if (isInventoryGeneralRequest) {
      return items.filter((item) => {
        const { selectable } = getInventoryRequestSelectionState(item, lockedOriginLocationName);
        return selectable;
      });
    }
    if (isInventoryBranchQuery) {
      // Para consultas de sucursal: seleccionables si están en sucursal y Disponibles
      return items.filter(
        (item) =>
          item.status === INV_STATUS_BRANCH_LABEL && item.availabilityStatus === 'Disponible',
      );
    }
    // Solo en almacén y disponibles (no bloqueadas ni en tránsito)
    return items.filter(isSelectableForWarehouseTransfer);
  }, [items, isInventoryBranchQuery, isInventoryGeneralRequest, lockedOriginLocationName]);

  const getItemSelectionState = useCallback(
    (item: InventoryItem) => {
      if (isInventoryGeneralRequest) {
        return getInventoryRequestSelectionState(item, lockedOriginLocationName);
      }
      if (isInventoryBranchQuery) {
        const selectable =
          item.status === INV_STATUS_BRANCH_LABEL && item.availabilityStatus === 'Disponible';
        return {
          selectable,
          reason: selectable
            ? 'Haz clic para seleccionar esta pieza'
            : `No se puede seleccionar: Estado "${item.status}" o Disponibilidad "${item.availabilityStatus}"`,
        };
      }
      const selectable = isSelectableForWarehouseTransfer(item);
      return {
        selectable,
        reason: selectable
          ? 'Haz clic para seleccionar esta pieza'
          : `No se puede seleccionar: estado de venta "${item.availabilityStatus}"`,
      };
    },
    [isInventoryBranchQuery, isInventoryGeneralRequest, lockedOriginLocationName],
  );

  const isAllSelected = useMemo(
    () =>
      selectableItems.length > 0 &&
      selectableItems.every((item) => selectedItems.includes(item.id)),
    [selectableItems, selectedItems],
  );

  const handleItemsPerPageChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value;
      onItemsPerPageChange(Number(value));
    },
    [onItemsPerPageChange],
  );

  const paginationInfo = useMemo(() => {
    const startItem = (pagination.currentPage - 1) * pagination.itemsPerPage + 1;
    const endItem = Math.min(
      pagination.currentPage * pagination.itemsPerPage,
      pagination.totalItems,
    );
    return { startItem, endItem };
  }, [pagination]);

  // Manejo de selección individual
  const handleSelectItem = useCallback(
    (id: string) => {
      const newSelectedItems = selectedItems.includes(id)
        ? selectedItems.filter((itemId) => itemId !== id)
        : [...selectedItems, id];

      onSelectItems(newSelectedItems);
    },
    [selectedItems, onSelectItems],
  );

  // Manejo de selección de todos los elementos seleccionables de la página actual
  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // Deseleccionar todos los elementos de la página actual
      const newSelectedItems = selectedItems.filter((id) => !items.some((item) => item.id === id));
      onSelectItems(newSelectedItems);
    } else {
      // Seleccionar solo los elementos seleccionables de la página actual
      const currentPageSelectableIds = selectableItems.map((item) => item.id);
      const newSelectedItems = [...selectedItems];

      currentPageSelectableIds.forEach((id) => {
        if (!newSelectedItems.includes(id)) {
          newSelectedItems.push(id);
        }
      });

      onSelectItems(newSelectedItems);
    }
  }, [isAllSelected, selectedItems, items, selectableItems, onSelectItems]);

  // Calcular número total de columnas para el mensaje de "no se encontraron productos"
  const getTotalColumns = useCallback(() => {
    let columns = 10; // Columnas base: Código, Nombre, Tipo, Fecha de registro, Nº Registro, Estado, Fecha Transfer, Transfer Code, Sucursal, Acción
    if (isAdmin && !isInventoryBranchQuery) columns += 1; // +1 para checkbox (warehouse)
    if (isAdmin && isInventoryBranchQuery) columns += 1; // +1 para checkbox (branch) - cuando hay onTransferSelected
    if (!isInventoryBranchQuery) columns += 1; // +1 para Estado Venta
    if (!isInventoryBranchQuery && warehouseAttributeColumnCount > 0) {
      columns += warehouseAttributeColumnCount;
    }
    return columns;
  }, [isAdmin, isInventoryBranchQuery, warehouseAttributeColumnCount]);

  // Función helper para obtener propiedades del chip según el estado
  const getChipProps = useCallback((status: string, isBranchQuery: boolean = false) => {
    if (isBranchQuery) {
      // Lógica para consulta de sucursal
      switch (status) {
        case 'Disponible':
          return { color: 'success' as const, className: 'bg-green-100 text-green-800' };
        case 'Vendido':
          return { color: 'info' as const, className: 'bg-blue-100 text-blue-800' };
        case INV_AVAILABILITY_BLOCKED_LABEL:
          return { color: 'warning' as const, className: 'bg-yellow-100 text-yellow-800' };
        case 'Pago Pendiente':
          return { color: 'warning' as const, className: 'bg-amber-100 text-amber-800' };
        default:
          return { color: 'default' as const, className: 'bg-gray-100 text-gray-800' };
      }
    } else {
      // Lógica para consulta de almacén
      switch (status) {
        case INV_STATUS_BRANCH_LABEL:
          return { color: 'success' as const, className: 'bg-green-100 text-green-800' };
        case 'En Transito':
          return { color: 'info' as const, className: 'bg-blue-100 text-blue-800' };
        case INV_STATUS_WAREHOUSE_LABEL:
          return { color: 'default' as const, className: 'bg-gray-100 text-gray-800' };
        default:
          return { color: 'default' as const, className: 'bg-gray-100 text-gray-800' };
      }
    }
  }, []);

  // Estilos comunes para checkboxes
  const getCheckboxStyles = useCallback((isEnabled: boolean) => {
    if (isEnabled) {
      return {
        opacity: 1,
        cursor: 'pointer',
      };
    } else {
      return {
        opacity: 0.3,
        cursor: 'not-allowed',
      };
    }
  }, []);

  return {
    selectableItems,
    isAllSelected,
    handleItemsPerPageChange,
    paginationInfo,
    handleSelectItem,
    handleSelectAll,
    getTotalColumns,
    getChipProps,
    getCheckboxStyles,
    getItemSelectionState,
  };
};
